import { useQuery } from "@apollo/client";
import { TFunction } from "i18next";
import { chain, sumBy, upperFirst } from "lodash";
import { useMemo } from "react";
import { match } from "ts-pattern";

import { getLegacyBenchmarkMarkerColor } from "components/PortfolioDashboard/Charts/utils";
import { ChartDisplayType } from "glue/Chart/chart.constants";
import {
    AssessmentVertical,
    ConsumptionBreakdownItem,
    GetConsumptionChartDataQuery,
    GetConsumptionChartDataQueryVariables,
    InsightsAsset,
    LegacyBenchmarkType,
    PropertyOwnershipType,
} from "graphql-types/graphql";
import { DASH_SEPARATOR } from "utils";
import { gramsToKg } from "utils/report.helpers";

import { GET_CONSUMPTION_CHART_DATA } from "./consumption.query";
import {
    BenchmarkSeries,
    ConsumptionChartData,
    ConsumptionSeries,
    verticals,
} from "./consumption.types";

export const formatConsumptionValue = (
    value: number | null,
    type: ChartDisplayType
) =>
    !value
        ? DASH_SEPARATOR
        : type === ChartDisplayType.ENERGY
        ? `${value.toFixed(2)} kWh`
        : `${value.toFixed(2)} kg CO2e`;

export const getConsumptionChartData = (
    consumption: ConsumptionBreakdownItem[],
    type: string
) => {
    const chartData: Record<
        AssessmentVertical,
        { ownershipType: PropertyOwnershipType; value: number }[]
    > = {} as Record<
        AssessmentVertical,
        { ownershipType: PropertyOwnershipType; value: number }[]
    >;

    chain(consumption)
        .groupBy(
            (breakdown) => `${breakdown.vertical}-${breakdown.ownershipType}`
        )
        .forEach((breakdownGroup, group) => {
            const total = sumBy(
                breakdownGroup,
                (breakdown) => breakdown.emission || 0
            );

            const calculatedTotal =
                type === ChartDisplayType.EMISSIONS ? gramsToKg(total) : total;

            const [vertical, ownershipType] = group.split("-") as [
                AssessmentVertical,
                PropertyOwnershipType
            ];

            chartData[vertical] = [
                ...(chartData[vertical] || []),
                { ownershipType, value: calculatedTotal },
            ];
        })
        .value();

    return chartData;
};

const getValue = (
    breakdown: Record<
        AssessmentVertical,
        { ownershipType: PropertyOwnershipType; value: number }[]
    >,
    vertical: AssessmentVertical,
    ownership: PropertyOwnershipType
): number =>
    breakdown[vertical]?.find((item) => item.ownershipType === ownership)
        ?.value || 0;

export const useConsumptionChartData = ({
    asset,
    type,
    filterTranslations,
    benchmarkTranslations,
    getRegionName,
    t,
}: {
    asset: InsightsAsset;
    type: ChartDisplayType;
    filterTranslations: Record<string, string>;
    benchmarkTranslations: Record<string, string>;
    getRegionName: (region: string) => string;
    t: TFunction;
}): {
    rawData: GetConsumptionChartDataQuery | undefined;
    loading: boolean;
    chartData: ConsumptionChartData[];
    benchmarksSeries: BenchmarkSeries[];
} => {
    const { data, loading } = useQuery<
        GetConsumptionChartDataQuery,
        GetConsumptionChartDataQueryVariables
    >(GET_CONSUMPTION_CHART_DATA, {
        variables: {
            assetId: asset.id,
            bfe: asset.externalId,
            from: new Date(2020, 0, 1),
        },
    });

    const chartData = useMemo(() => {
        const consumptionKey =
            type === ChartDisplayType.EMISSIONS ? "emission" : "energy";

        const projectedKey =
            type === ChartDisplayType.EMISSIONS
                ? "projectedEmission"
                : "projectedEnergy";

        return (
            data?.getConsumptionChartData.flatMap((yearlyConsumption) => {
                const data = getConsumptionChartData(
                    yearlyConsumption[
                        consumptionKey
                    ] as ConsumptionBreakdownItem[],
                    type
                );
                const projectedData = getConsumptionChartData(
                    yearlyConsumption[
                        projectedKey
                    ] as ConsumptionBreakdownItem[],
                    type
                );

                const totals = verticals.reduce((acc, vertical) => {
                    [
                        PropertyOwnershipType.LANDLORD,
                        PropertyOwnershipType.TENANT,
                    ].forEach((ownership) => {
                        const key = `${vertical.toLowerCase()}${upperFirst(
                            ownership.toLowerCase()
                        )}`;

                        const actual = getValue(data, vertical, ownership);
                        const projected = getValue(
                            projectedData,
                            vertical,
                            ownership
                        );
                        const total = actual + Math.max(0, projected - actual);

                        if (total) acc[key] = total;
                    });

                    return acc;
                }, {} as Record<string, number>);

                return {
                    period: yearlyConsumption.year,
                    ...totals,
                };
            }) ?? []
        );
    }, [data?.getConsumptionChartData, type]);

    const benchmarksSeries = useMemo(() => {
        const consumptionKey =
            type === ChartDisplayType.EMISSIONS
                ? "co2BenchmarkMethod1"
                : "kwhMethod1";
        return (
            chain(data?.getLegacyNextAssetBenchmarks)
                .orderBy((b) => b.startTs)
                .groupBy((b) => b.segment)
                .map((benchmark) => {
                    const area =
                        asset.area ??
                        sumBy(asset.properties, (p) => p.area ?? 0);
                    const data = benchmark.map((b) => b[consumptionKey] * area);

                    const segmentLabel = match(benchmark[0])
                        .with(
                            {
                                benchmarkType:
                                    LegacyBenchmarkType.BBR_PROPERTY_TYPE,
                            },
                            ({ benchmarkType, segment }) =>
                                benchmarkTranslations[
                                    `${benchmarkType}-${segment}`
                                ]
                        )
                        .with(
                            { benchmarkType: LegacyBenchmarkType.NATIONAL },
                            ({ segment }) => getRegionName(segment)
                        )
                        .with(
                            {
                                benchmarkType:
                                    LegacyBenchmarkType.BUILDING_SIZE,
                            },
                            ({ segment }) =>
                                `${segment} ${t("filter.m2", "m²")}`
                        )
                        .with(
                            { benchmarkType: LegacyBenchmarkType.REGION },
                            ({ segment }) => segment.split("_")[1]
                        )
                        .otherwise(
                            ({ segment }) =>
                                filterTranslations[
                                    segment as LegacyBenchmarkType
                                ] ?? segment
                        );

                    const benchmarkLabel =
                        filterTranslations[
                            benchmark[0].benchmarkType as LegacyBenchmarkType
                        ] ?? benchmark;

                    return {
                        id: `${benchmark[0].benchmarkType}-${benchmark[0].segment}`,
                        label: `${benchmarkLabel} - ${segmentLabel}`,
                        benchmark: benchmark[0].benchmarkType,
                        segment: benchmark[0].segment,
                        color: getLegacyBenchmarkMarkerColor(
                            benchmark[0].benchmarkType as LegacyBenchmarkType
                        ),
                        data,
                        showMark: false,
                    } as BenchmarkSeries;
                })
                .value() ?? []
        );
    }, [
        asset,
        data?.getLegacyNextAssetBenchmarks,
        type,
        filterTranslations,
        getRegionName,
        benchmarkTranslations,
        t,
    ]);

    return { rawData: data, benchmarksSeries, chartData, loading };
};

export const getConsumptionSeries = (
    t: TFunction,
    dataKeys: string[],
    type: ChartDisplayType
): ConsumptionSeries[] => {
    const commonSeries = {
        area: true,
        stack: "total",
        showMark: false,
        valueFormatter: (value: number | null) =>
            formatConsumptionValue(value, type),
    };

    return [
        {
            id: "consumption-electricityLandlord",
            label:
                t("assessmentOverview.electricity.title", "Electricity") +
                " " +
                t("common.landlord", "landlord"),
            dataKey: "electricityLandlord",
            vertical: AssessmentVertical.ELECTRICITY,
            color: "#E39F22",
            ...commonSeries,
        },
        {
            id: "consumption-electricityTenant",
            label:
                t("assessmentOverview.electricity.title", "Electricity") +
                " " +
                t("common.tenant", "tenant"),
            dataKey: "electricityTenant",
            vertical: AssessmentVertical.ELECTRICITY,
            color: "#FFB224",
            ...commonSeries,
        },
        {
            id: "consumption-heatingLandlord",
            label:
                t("assessmentOverview.heat.title", "Heating") +
                " " +
                t("common.landlord", "landlord"),
            dataKey: "heatingLandlord",
            vertical: AssessmentVertical.HEATING,
            color: "#D72C2C",
            ...commonSeries,
        },
        {
            id: "consumption-heatingTenant",
            label:
                t("assessmentOverview.heat.title", "Heating") +
                " " +
                t("common.tenant", "tenant"),
            dataKey: "heatingTenant",
            vertical: AssessmentVertical.HEATING,
            color: "#F94F4F",
            ...commonSeries,
        },
        {
            id: "consumption-waterLandlord",
            label:
                t("assessmentOverview.water.title", "Water") +
                "  " +
                t("common.landlord", "landlord"),
            dataKey: "waterLandlord",
            vertical: AssessmentVertical.WATER,
            color: "#4D9BFF",
            ...commonSeries,
        },
        {
            id: "consumption-waterTenant",
            label:
                t("assessmentOverview.water.title", "Water") +
                "  " +
                t("common.tenant", "tenant"),
            dataKey: "waterTenant",
            vertical: AssessmentVertical.WATER,
            color: "#2FC3DD",
            ...commonSeries,
        },
    ].filter((serie) => dataKeys.includes(serie.dataKey));
};
